import React, { useMemo } from 'react'
import { styled, View, YStack, useMedia, H2, PillCarousel, PillInstanceType } from '@red-ui/components'
import { Content } from '../'
import { useSearchParams } from 'react-router-dom'
import { pushToGTM } from '../../helpers/windowMethods'

type DiscoveryHeaderProps = {
  title: string
  categories?: PillInstanceType['pillItem'][]
}

const InnerShadow = styled(View, {
  display: 'block',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '$2',
  pointerEvents: 'none',
  backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.075), transparent)',
  zIndex: 1,
})

export const DiscoveryHeader = ({ title, categories = [] }: DiscoveryHeaderProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const media = useMedia()

  const clearFilters = () => {
    setSearchParams((prev) => ({
      ...prev,
      category: [],
    }))
  }

  const onPress = (filter: PillInstanceType['pillItem']) => {
    const activeFilters = searchParams.get('category')?.split(',').map(decodeURIComponent) ?? []
    const newFilters = activeFilters.includes(filter.name)
      ? activeFilters.filter((item) => item !== filter.name)
      : [...activeFilters, filter.name]

    pushToGTM({
      event: 'selectDiscoveryFilter',
      filterDetails: {
        selectedFilterName: filter.name,
        selectedFilterCount: filter.count,
        selectedFilterAction: activeFilters.includes(filter.name) ? 'disable' : 'enable',
        oldActiveFilters: activeFilters,
        newActiveFilters: filter.name === 'All' ? [] : newFilters,
      },
    })

    if (filter.name === 'All') {
      clearFilters()
      return
    }

    if (newFilters.length === categories.length - 1 || newFilters.length === 0) {
      clearFilters()
      return
    }

    setSearchParams((prev) => ({
      ...prev,
      category: newFilters.join(','),
    }))
  }

  const selectedPills = useMemo(() => {
    const activeFilters = searchParams.get('category')?.split(',').map(decodeURIComponent) ?? []
    if (activeFilters.length === 0) {
      return categories[0] ? [categories[0]] : []
    }
    return categories.filter((category) => activeFilters.includes(category.name))
  }, [categories, searchParams])

  return (
    <YStack backgroundColor={'$red6'} paddingTop="$2" paddingBottom="$2" position="relative">
      <InnerShadow />
      <Content>
        <H2
          tag="H1"
          display="flex"
          color={'$white'}
          margin={'$0'}
          fontWeight={600}
          fontSize={media.mobile ? '$6' : '$8'}
          whiteSpace="nowrap"
          lineHeight={media.mobile ? 24 : undefined}>
          {title}
        </H2>
        <PillCarousel items={categories} onPress={onPress} selected={selectedPills} marginTop={'$2'} />
      </Content>
    </YStack>
  )
}
